/**
 * One-shot data loading for a page: call the API when the page mounts, show
 * loading / error / data. The pattern every read-only page uses until there
 * is a reason for a cache (D-030 — no query library yet; add one when two
 * pages want the same data at once, not before).
 *
 * The loader receives an AbortSignal and must pass it to the API call, so a
 * page that unmounts mid-request does not set state on a dead component.
 */

import { useCallback, useEffect, useState } from 'react';
import { ApiError } from './api.js';

export type Load<T> =
  | { readonly state: 'loading' }
  | { readonly state: 'ok'; readonly data: T }
  | { readonly state: 'error'; readonly message: string; readonly forbidden: boolean; /** The API's status when it answered (404: the record is not there); undefined when it did not answer. */ readonly status?: number };

/**
 * What a page draws while a `reload` is out: the data it last had, so a grid never unmounts under a write and
 * a row's panel keeps what it is doing (the PO grid, 2026-11-03); `null` before the first read and on an error.
 */
export function shownWhileLoading<T>(load: Load<T>, last: T | null): T | null {
  if (load.state === 'ok') return load.data;
  return load.state === 'loading' ? last : null;
}

export interface Loaded<T> {
  readonly load: Load<T>;
  /** Run the loader again, e.g. after a write. */
  readonly reload: () => void;
  /** Replace the loaded data locally after a write that returned the new row. */
  readonly setData: (update: (previous: T) => T) => void;
}

export interface LoadOptions {
  /**
   * False holds the read back — the state stays `loading` and nothing is fetched — until the page
   * knows what to ask for (contexts/25). The lists use it: fetching before the saved columns are
   * known meant every list loaded twice, once with the fallback columns and once with the person's.
   */
  readonly enabled?: boolean;
}

export function useLoad<T>(loader: (signal: AbortSignal) => Promise<T>, deps: readonly unknown[] = [], options: LoadOptions = {}): Loaded<T> {
  const [load, setLoad] = useState<Load<T>>({ state: 'loading' });
  const [generation, setGeneration] = useState(0);
  const enabled = options.enabled ?? true;

  useEffect(() => {
    if (!enabled) {
      setLoad({ state: 'loading' });
      return;
    }
    const abort = new AbortController();
    setLoad({ state: 'loading' });
    loader(abort.signal)
      .then((data) => setLoad({ state: 'ok', data }))
      .catch((err: unknown) => {
        if (abort.signal.aborted) return;
        setLoad({
          state: 'error',
          message: err instanceof ApiError ? err.message : 'Unexpected error calling the API.',
          forbidden: err instanceof ApiError && err.forbidden,
          ...(err instanceof ApiError && err.status !== undefined ? { status: err.status } : {}),
        });
      });
    return () => abort.abort();
    // The caller's deps decide when to refetch; the loader identity does not.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [generation, enabled, ...deps]);

  const reload = useCallback(() => setGeneration((g) => g + 1), []);
  const setData = useCallback(
    (update: (previous: T) => T) => setLoad((l) => (l.state === 'ok' ? { state: 'ok', data: update(l.data) } : l)),
    [],
  );

  return { load, reload, setData };
}
